import axios from 'axios';
import Api from '../api';
import {
  MaterialSection,
  SampleTagResponse,
  SampleCreatedWith,
  DevelopmentType,
} from '../../Components/SampleTags/AddTags/types';
import { FILE_VALIDATION } from '../../Components/SampleTags/AddTags/constants';

const USERS = '/users';
const SAMPLE_TAGS = '/sample-tags';

export interface SampleTagCreateData {
  type_of_sample_tag:
    | 'First Sample'
    | 'Proto Development'
    | 'Fit'
    | 'Pre-Production'
    | 'TOP'
    | 'Block';
  product_image?: string;
  style_number?: string;
  style_name?: string | null;
  division?: string | null;
  department?: string | null;
  category?: string | null;
  class?: string | null;
  coo?: string | null;
  shipment_mode?: string | null;
  fn_exclusive?: boolean;
  fit_sample?: boolean;
  sample_created_with?: Partial<SampleCreatedWith>;
  self?: MaterialSection;
  vendor_style?: string;
  type_of_development?: DevelopmentType;
  sample_size?: string;
  color_wash?: string;
  po_number?: string;
  product_sku?: string;
  date_sent?: string;
  cut_date?: string;
  custom_cut_lead_time?: number | null;
  cc_lead_time_date?: string | null;
  junior_cost?: number;
  junior_moq?: number | null;
  plus_cost?: number;
  plus_moq?: number | null;
  contrast?: MaterialSection;
  lining?: MaterialSection;
  filling?: MaterialSection;
  coating?: MaterialSection;
  backing?: MaterialSection;
  comments?: string;
  po_due_date?: string;
  po_line_name?: string;
  po_line_color?: string;
  po_line_size?: string;
  block_number?: string;
  pants_attribute?: string;
  tops_attribute?: string;
  self_shell_fabric_code?: string;
  vendor_catalog_item_id?: string | null;
}

export interface SampleTagListResponse {
  tags: SampleTagResponse[];
  count: number;
}

export interface SampleTagListParams {
  page?: number;
  limit?: number;
  po_number?: string;
  sku?: string;
  type_of_sample_tag?: string;
  order?: string;
  dir?: 'ASC' | 'DESC';
  style_number?: string;
  include_signed_urls?: boolean;
  with_vendor_catalog?: boolean;
}

export interface GeneratePhotoUploadUrlRequest {
  imageContentType: (typeof FILE_VALIDATION.ACCEPTED_TYPES)[number];
}

export interface GeneratePhotoUploadUrlResponse {
  signedUrl: string;
  fileKey: string;
  contentType: string;
}

export interface PhotoUploadResult {
  success: boolean;
  fileKey?: string;
  error?: string;
}

const SampleTagsAPI = {
  /**
   * Get list of sample tags for a user
   */
  async list(
    userId: string,
    params?: SampleTagListParams,
  ): Promise<SampleTagListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.po_number) queryParams.append('po_number', params.po_number);
    if (params?.sku) queryParams.append('sku', params.sku);
    if (params?.type_of_sample_tag)
      queryParams.append('type_of_sample_tag', params.type_of_sample_tag);
    if (params?.order) queryParams.append('order', params.order);
    if (params?.dir) queryParams.append('dir', params.dir);
    if (params?.style_number)
      queryParams.append('style_number', params.style_number);
    if (params?.include_signed_urls !== undefined)
      queryParams.append(
        'include_signed_urls',
        params.include_signed_urls.toString(),
      );
    if (params?.with_vendor_catalog !== undefined)
      queryParams.append(
        'with_vendor_catalog',
        params.with_vendor_catalog.toString(),
      );

    const query = queryParams.toString();
    const url = `${USERS}/${userId}${SAMPLE_TAGS}${query ? `?${query}` : ''}`;

    return Api.get(url);
  },

  /**
   * Get a single sample tag with full details
   */
  async getById(userId: string, tagId: string): Promise<SampleTagResponse> {
    const url = `${USERS}/${userId}${SAMPLE_TAGS}/${tagId}`;
    return Api.get(url);
  },

  /**
   * Create a new sample tag
   */
  async create(
    userId: string,
    tagData: SampleTagCreateData,
  ): Promise<SampleTagResponse> {
    const url = `${USERS}/${userId}${SAMPLE_TAGS}`;
    return Api.post(url, { data: tagData });
  },

  /**
   * Update an existing sample tag
   */
  async update(
    userId: string,
    tagId: string,
    updates: Partial<SampleTagCreateData>,
  ): Promise<SampleTagResponse> {
    const url = `${USERS}/${userId}${SAMPLE_TAGS}/${tagId}`;
    return Api.patch(url, { data: updates });
  },

  /**
   * Generate a pre-signed URL for photo upload
   */
  async generatePhotoUploadUrl(
    userId: string,
    request: GeneratePhotoUploadUrlRequest,
  ): Promise<GeneratePhotoUploadUrlResponse> {
    const response = await Api.post(
      `/users/${userId}/sample-tags/photos/upload-url`,
      { data: request },
    );
    return response;
  },

  /**
   * Upload photo directly to S3 using pre-signed URL
   */
  async uploadPhotoToS3(
    signedUrl: string,
    file: File,
    contentType: string,
  ): Promise<void> {
    try {
      await axios.put(signedUrl, file, {
        headers: {
          'Content-Type': contentType,
        },
        transformRequest: (data) => data,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          `Upload failed: ${error.response?.statusText || 'Unknown error'} (${error.response?.status || 'No status'})`,
        );
      }
    }
  },

  /**
   * Generate a signed URL for reading a sample tag photo
   */
  async generatePhotoReadUrl(
    userId: string,
    fileKey: string,
  ): Promise<{ signedUrl: string; fileKey: string; expiresIn: number }> {
    const response = await Api.get(
      `${USERS}/${userId}${SAMPLE_TAGS}/photos/${fileKey}/read-url`,
    );
    return response;
  },

  /**
   * Proxy endpoint to fetch CloudFront-protected images
   * Supports both single and batch requests
   * @param userId - User ID
   * @param imageUrls - Array of S3 keys (can be single item)
   * @returns Array of base64-encoded images
   */
  async getImagesViaProxy(
    userId: string,
    imageUrls: string[],
  ): Promise<
    Array<{ url: string; data?: string; contentType?: string; error?: string }>
  > {
    const response = await Api.post(
      `${USERS}/${userId}${SAMPLE_TAGS}/photos/proxy`,
      {
        data: { imageUrls },
      },
    );
    return response.images;
  },

  /**
   * Complete photo upload process (get URL + upload file)
   */
  async uploadPhoto(userId: string, file: File): Promise<PhotoUploadResult> {
    try {
      const urlResponse = await this.generatePhotoUploadUrl(userId, {
        imageContentType:
          file.type as (typeof FILE_VALIDATION.ACCEPTED_TYPES)[number],
      });

      await this.uploadPhotoToS3(
        urlResponse.signedUrl,
        file,
        urlResponse.contentType,
      );

      return {
        success: true,
        fileKey: urlResponse.fileKey,
      };
    } catch (error) {
      console.error('Photo upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  },

  /**
   * Get or create a sample tag from a vendor catalog item.
   * Backend handles checking for existing tag, comparing data,
   * and creating/updating as needed.
   */
  async getOrCreateFromVendorCatalogItem(
    userId: string,
    vendorCatalogItemId: string,
  ): Promise<SampleTagResponse> {
    const url = `${USERS}/${userId}${SAMPLE_TAGS}/vendor-catalog/${vendorCatalogItemId}`;
    return Api.post(url);
  },

  /**
   * Bulk get or create sample tags from vendor catalog items
   */
  async bulkGetOrCreateFromVendorCatalogItems(
    userId: string,
    vendorCatalogItemIds: string[],
  ): Promise<{
    tags: SampleTagResponse[];
    errors: Array<{ vendor_catalog_item_id: string; error: string }>;
  }> {
    const url = `${USERS}/${userId}${SAMPLE_TAGS}/vendor-catalog`;
    return Api.post(url, {
      data: { vendor_catalog_item_ids: vendorCatalogItemIds },
    });
  },
};

export default SampleTagsAPI;
